// Query keys for React Query cache management of communications
// Centralized query key factory following the tenant contacts pattern

import type { CallListParams } from '../api/callApi';
import type { EmailListParams } from '../api/emailApi';
import type { NotificationListParams } from '../api/notificationApi';
import type { SMSThreadParams } from '../api/smsApi';
import type { UnifiedListParams } from '../api/unifiedApi';

export const communicationKeys = {
  // Emails
  emails: {
    all: ['emails'] as const,
    lists: () => [...communicationKeys.emails.all, 'list'] as const,
    list: (params?: EmailListParams) => [...communicationKeys.emails.lists(), params] as const,
    details: () => [...communicationKeys.emails.all, 'detail'] as const,
    detail: (emailId: string) => [...communicationKeys.emails.details(), emailId] as const,
    threads: () => [...communicationKeys.emails.all, 'thread'] as const,
    thread: (emailId: string) => [...communicationKeys.emails.threads(), emailId] as const,
    autoFollowUp: (emailId: string) =>
      [...communicationKeys.emails.all, 'auto-follow-up', emailId] as const,
  },

  // Calls
  calls: {
    all: ['calls'] as const,
    lists: () => [...communicationKeys.calls.all, 'list'] as const,
    list: (params?: CallListParams) => [...communicationKeys.calls.lists(), params] as const,
    details: () => [...communicationKeys.calls.all, 'detail'] as const,
    detail: (id: string) => [...communicationKeys.calls.details(), id] as const,
    // Mutation key, not a query key: it makes in-flight bridge requests countable across
    // every component that can start one.
    bridge: () => [...communicationKeys.calls.all, 'bridge'] as const,
  },

  // SMS
  sms: {
    all: ['sms'] as const,
    threads: () => [...communicationKeys.sms.all, 'thread'] as const,
    threadList: (params?: SMSThreadParams) => [...communicationKeys.sms.threads(), params] as const,
    threadDetails: () => [...communicationKeys.sms.all, 'threadDetail'] as const,
    threadDetail: (threadId: string, params?: SMSThreadParams) =>
      [...communicationKeys.sms.threadDetails(), threadId, params] as const,
  },

  // Templates (for later implementation)
  templates: {
    email: {
      all: ['emailTemplates'] as const,
      lists: () => [...communicationKeys.templates.email.all, 'list'] as const,
      list: (params?: any) => [...communicationKeys.templates.email.lists(), params] as const,
      detail: (id: string) => [...communicationKeys.templates.email.all, 'detail', id] as const,
    },
    sms: {
      all: ['smsTemplates'] as const,
      lists: () => [...communicationKeys.templates.sms.all, 'list'] as const,
      list: (params?: any) => [...communicationKeys.templates.sms.lists(), params] as const,
      detail: (id: string) => [...communicationKeys.templates.sms.all, 'detail', id] as const,
    },
  },

  // Signatures (for later implementation)
  signatures: {
    all: ['emailSignatures'] as const,
    lists: () => [...communicationKeys.signatures.all, 'list'] as const,
    list: (params?: any) => [...communicationKeys.signatures.lists(), params] as const,
    detail: (id: string) => [...communicationKeys.signatures.all, 'detail', id] as const,
  },

  // Unified Inbox
  unified: {
    all: ['unified'] as const,
    lists: () => [...communicationKeys.unified.all, 'list'] as const,
    list: (params?: UnifiedListParams) => [...communicationKeys.unified.lists(), params] as const,
  },

  // AI Generations
  aiGenerations: {
    all: ['aiGenerations'] as const,
    details: () => [...communicationKeys.aiGenerations.all, 'detail'] as const,
    detail: (generationId: string) =>
      [...communicationKeys.aiGenerations.details(), generationId] as const,
  },

  // Twilio caller ID verification
  twilioVerification: {
    all: ['twilioVerification'] as const,
    readiness: () => [...communicationKeys.twilioVerification.all, 'readiness'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...communicationKeys.notifications.all, 'list'] as const,
    list: (params?: NotificationListParams) =>
      [...communicationKeys.notifications.lists(), params] as const,
    // No hook reads this exact key. It is a prefix of @commander/notifications'
    // unread-count key, so invalidating it refreshes the bell's badge.
    unreadCount: () => [...communicationKeys.notifications.all, 'unread-count'] as const,
  },
};
